import React, { useState, useEffect } from 'react';
import { Box, Backdrop, Typography, Button, Divider, IconButton, Paper, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../../assets/rounded_logo.svg';
import { trackFeatureUsage } from '../../services/FeatureTrackingService';
import SecondaryButton from '../SecondaryButton';
import { useAuth } from '../../hooks/useAuth';

interface Task {
  id: string;
  action_type: string;
  listing_id: string;
  should_post_on_zillow?: boolean;
  should_post_on_facebook?: boolean;
  should_post_on_apartments?: boolean;
  should_post_on_craiglist?: boolean;
  stale?: boolean;
  zillow_listing_link?: string | null;
  facebook_listing_link?: string | null;
  apartments_listing_link?: string | null;
  craiglist_listing_link?: string | null;
  all_tasks_complete?: boolean;
  created_at: string;
}

interface ListingInfoOverlayProps {
  open: boolean;
  tasks: Task[];
  currentListingId: string;
  description?: string | null;
  screeningLink?: string | null;
  onClose: () => void;
}

const ListingInfoOverlay: React.FC<ListingInfoOverlayProps> = ({ open, tasks, onClose, currentListingId, description, screeningLink }) => {
  const { user } = useAuth();
  const [copiedDescription, setCopiedDescription] = useState(false);
  const [copiedScreeningLink, setCopiedScreeningLink] = useState(false);

  const handleCopyDescription = async () => {
    if (!description) return;
    try {
      await navigator.clipboard.writeText(description);
      setCopiedDescription(true);
      setTimeout(() => setCopiedDescription(false), 2000);
      if (user?.id) {
        trackFeatureUsage(user.id, 'Copy Listing Description -App');
      }
    } catch (err) {
      console.error('Failed to copy description:', err);
    }
  };

  const handleCopyScreeningLink = async () => {
    if (!screeningLink) return;
    try {
      await navigator.clipboard.writeText(screeningLink);
      setCopiedScreeningLink(true);
      setTimeout(() => setCopiedScreeningLink(false), 2000);
      if (user?.id) {
        trackFeatureUsage(user.id, 'Copy Listing Screening Link -App');
      }
    } catch (err) {
      console.error('Failed to copy screening link:', err);
    }
  };
 
  return (
    <Backdrop
      open={open}
      onClick={onClose}
      sx={{ zIndex: 1300, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Paper
            sx={{
              p: 0,
              borderRadius: 4,
              width: 400,
              maxWidth: '100%',
              maxHeight: '95vh',
              overflowY: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 2,
                  borderBottom: '1px solid #eee',
                }}
              >
                <Box>
                  <img src={logo} alt="Heyy Property Logo" style={{ height: '48px', width: 'auto' }} />
                </Box>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Box>

            
            
            <Box sx={{ px: 3, py: 2 }}>
            
            <Typography variant="h6" fontWeight={600} mb={1}>
                Listing Information
            </Typography>
            <Typography variant="caption" fontWeight={300} mb={4}>Facebook supports links in descriptions. Zillow does not. You can send links to prospects on Zillow and Facebook.</Typography>
            
            {/* Screening Link Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Screening Link
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                bgcolor: 'grey.50',
                mb: 1
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    flex: 1,
                    fontSize: '0.875rem'
                  }}
                >
                  {screeningLink?.replace(/^https?:\/\//, '') || 'No screening link available'}
                </Typography>
                <SecondaryButton
                  variant="contained"
                  size="small"
                  sx={{
                    fontFamily: 'Inter',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: 2,
                    boxShadow: 'none',
                    border: '1px solid black',
                    color: 'white',
                    background: 'black',
                    minWidth: '60px',
                    ml: 2,
                    '&:hover': {
                      background: '#333',
                      border: '1px solid black',
                      borderRadius: 2,
                      color: 'white',
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyScreeningLink();
                  }}
                >
                  {copiedScreeningLink ? 'Copied!' : 'Copy'}
                </SecondaryButton>
              </Box>
            </Box>

            {/* Listing Description Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Description
              </Typography>
              <Box sx={{ 
                p: 2,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                bgcolor: 'grey.50',
                mb: 1
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    mb: 2
                  }}
                >
                  {description ? `${description.substring(0, 110)}${description.length > 110 ? '...' : ''}` : 'No description available'}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <SecondaryButton
                    variant="contained"
                    size="small"
                    sx={{
                      fontFamily: 'Inter',
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid black',
                      color: 'white',
                      background: 'black',
                      minWidth: '60px',
                      '&:hover': {
                        background: '#333',
                        border: '1px solid black',
                        borderRadius: 2,
                        color: 'white',
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyDescription();
                    }}
                  >
                    {copiedDescription ? 'Copied!' : 'Copy'}
                  </SecondaryButton>
                </Box>
              </Box>
            </Box>

            {/* OKAY Button */}
            <Box mt={4} display="flex" justifyContent="flex-end">
              <Button
                fullWidth
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: '#000',
                  color: '#fff',
                  fontWeight: 500,
                  fontSize: 16,
                  py: 1.5,
                  borderRadius: '999px',
                  '&:hover': { backgroundColor: '#111' },
                }}
              >
                Okay
              </Button>
            </Box>
            </Box>
            </Box>
          </Paper>
        </Box>
      </Slide>  
    </Backdrop>
  );
};

export default ListingInfoOverlay;
